// ADMIN-INSIGHTS (design docs/design/2026-10-30-admin-insights-agent.md, v1): "Ask" — owners and
// admins ask about the company (spend per DE, budgets, tasks done and in progress by people vs DEs
// vs agents, approvals) and get the number in seconds, with where it comes from. Not a DE and not
// an agent: no seat, no chart card, no Team row — the system answering as the person. Members
// never see it: `useAsk()` is null for them, so every entry point (Home and KPIs buttons, ⌘K)
// renders nothing.
//
// One host per company shell; the panel opens over whatever page is showing. An answer can be
// pinned to Home (a small modal: title, how it is drawn, for me or every admin), where the
// scheduled refresh keeps it current.
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, PinIcon, SendIcon, SparklesIcon } from "lucide-react";
import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import { problemText } from "../ops-parts";
import { defaultViz, type InsightResult, InsightView, type InsightViz } from "./insight-view";

type Answer = OperationOutput<"insights.ask">;

export const insightsKey = (co: string) => ["insights", co] as const;
export const widgetsKey = (co: string) => ["insights", co, "widgets"] as const;

const AskContext = createContext<((question?: string) => void) | null>(null);

/** Opens Ask (optionally with a question asked at once); null for anyone but owners and admins. */
export function useAsk(): ((question?: string) => void) | null {
  return useContext(AskContext);
}

/** The page a number comes from (the result's `link`). */
export function sourceHref(slug: string, link: string): string {
  return link.startsWith("/inbox") ? link : `/c/${slug}${link}`;
}

export function AskHost({
  api,
  company,
  children,
}: {
  api: Api;
  company: CompanyAccess;
  children: ReactNode;
}) {
  const manager = isManager(company);
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState<{ q: string; n: number } | null>(null);
  const openAsk = useCallback((question?: string) => {
    setSeed(question ? { q: question, n: Date.now() } : null);
    setOpen(true);
  }, []);
  return (
    <AskContext.Provider value={manager ? openAsk : null}>
      {children}
      {manager ? (
        // Keyed by company: switching companies starts a fresh thread (no answer or pin carries).
        <AskPanel
          key={company.id}
          api={api}
          company={company}
          open={open}
          onOpenChange={setOpen}
          seed={seed}
        />
      ) : null}
    </AskContext.Provider>
  );
}

/** The header button (Home, KPIs): nothing for members. */
export function AskButton() {
  const ask = useAsk();
  if (!ask) return null;
  return (
    <Button variant="ghost" size="sm" data-slot="ask-open" onClick={() => ask()}>
      <SparklesIcon data-icon="inline-start" />
      Ask
    </Button>
  );
}

function AskPanel({
  api,
  company,
  open,
  onOpenChange,
  seed,
}: {
  api: Api;
  company: CompanyAccess;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seed: { q: string; n: number } | null;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [thread, setThread] = useState<Answer[]>([]);
  const [pinning, setPinning] = useState<Answer | null>(null);
  const end = useRef<HTMLDivElement | null>(null);
  const catalog = useQuery({
    queryKey: [...insightsKey(co), "catalog"],
    queryFn: () => api.client.insights.catalog({ params: { co } }),
    enabled: open,
    staleTime: 3_600_000,
  });
  const history = useQuery({
    queryKey: [...insightsKey(co), "asks"],
    queryFn: () => api.client.insights.asks({ params: { co } }),
    enabled: open,
  });
  const ask = useMutation({
    mutationFn: (question: string) =>
      api.client.insights.ask({ params: { co }, body: { question } }),
    onSuccess: (a) => {
      setThread((t) => [...t, a]);
      setText("");
      void qc.invalidateQueries({ queryKey: [...insightsKey(co), "asks"] });
    },
    onError: (e) => toast.add({ title: problemText(e, "Ask did not answer"), type: "error" }),
  });
  const send = (q: string) => {
    const question = q.trim();
    if (question && !ask.isPending) ask.mutate(question);
  };
  // A question handed in by the opener (⌘K, a widget's "explain") is asked at once.
  const seeded = useRef<number | null>(null);
  useEffect(() => {
    if (open && seed && seeded.current !== seed.n) {
      seeded.current = seed.n;
      send(seed.q);
    }
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on each new answer
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end" });
  }, [thread.length]);
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(text);
  };
  const earlier = (history.data?.data ?? []).filter((h) => !thread.some((t) => t.id === h.id));
  const examples = catalog.data?.examples ?? [];
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent data-slot="ask-panel" className="gap-0 sm:max-w-md">
          <SheetHeader className="border-b border-border">
            <SheetTitle className="flex items-center gap-1.5">
              <SparklesIcon className="size-4 text-muted-foreground" aria-hidden />
              Ask
              <InfoTip label="About Ask">
                Answers from the company's own numbers: agent spend, budgets, tasks by people, DEs
                and agents, approvals. Owners and admins only. It reads; it never changes a setting.
                Pin an answer to keep it on Home, refreshed every hour.
              </InfoTip>
            </SheetTitle>
            <SheetDescription className="sr-only">
              Ask a question about the company's numbers
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
            {thread.length === 0 ? (
              <div className="flex flex-col gap-2" data-slot="ask-examples">
                <p className="text-[13px] text-muted-foreground">Try one:</p>
                <div className="flex flex-wrap gap-1.5">
                  {examples.map((x) => (
                    <Button key={x} variant="outline" size="sm" onClick={() => send(x)}>
                      {x}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
            {thread.map((a) => (
              <AnswerCard
                key={a.id}
                answer={a}
                slug={company.slug}
                onPin={() => setPinning(a)}
                onAsk={send}
              />
            ))}
            {ask.isPending ? (
              <p className="text-[13px] text-muted-foreground" role="status">
                Working it out…
              </p>
            ) : null}
            {earlier.length > 0 ? (
              <details className="text-[13px]" data-slot="ask-earlier">
                <summary className="cursor-pointer text-muted-foreground">
                  Earlier ({earlier.length})
                </summary>
                <ul className="mt-1 flex flex-col">
                  {earlier.slice(0, 20).map((h) => (
                    <li key={h.id}>
                      <button
                        type="button"
                        className="w-full truncate rounded-sm px-1 py-0.5 text-left hover:bg-muted"
                        title="Ask again"
                        onClick={() => send(h.question)}
                      >
                        {h.question}
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            <div ref={end} />
          </div>
          <form onSubmit={onSubmit} className="flex items-center gap-2 border-t border-border p-3">
            <Input
              aria-label="Your question"
              placeholder="Ask about spend, budgets, tasks…"
              value={text}
              maxLength={500}
              onChange={(e) => setText(e.target.value)}
              data-slot="ask-input"
            />
            <Button
              type="submit"
              size="icon"
              aria-label="Ask"
              disabled={!text.trim() || ask.isPending}
            >
              <SendIcon />
            </Button>
          </form>
          {history.data ? (
            <p className="px-3 pb-2 text-xs text-muted-foreground">
              {history.data.left_today} questions left today
            </p>
          ) : null}
        </SheetContent>
      </Sheet>
      {pinning?.result ? (
        <PinDialog api={api} co={co} result={pinning.result} onClose={() => setPinning(null)} />
      ) : null}
    </>
  );
}

function AnswerCard({
  answer: a,
  slug,
  onPin,
  onAsk,
}: {
  answer: Answer;
  slug: string;
  onPin: () => void;
  onAsk: (q: string) => void;
}) {
  return (
    <article data-slot="ask-answer" data-via={a.via} className="group flex flex-col gap-1.5">
      <p className="self-end rounded-lg bg-muted px-2.5 py-1 text-[13px]">{a.question}</p>
      <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-3 py-2">
        <p className="text-[13px]">{a.answer}</p>
        {a.result ? <InsightView result={a.result} showNote={false} /> : null}
        {a.suggestions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {a.suggestions.map((s) => (
              <Button key={s} variant="outline" size="sm" onClick={() => onAsk(s)}>
                {s}
              </Button>
            ))}
          </div>
        ) : null}
        {a.result ? (
          <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 focus-within:opacity-100">
            <Button variant="ghost" size="sm" data-slot="ask-pin" onClick={onPin}>
              <PinIcon data-icon="inline-start" />
              Pin to Home
            </Button>
            <Button
              variant="ghost"
              size="sm"
              // The result's own page (a path the API names; not a typed route here).
              render={<Link to={sourceHref(slug, a.result.link) as "/"} />}
              data-slot="ask-source"
            >
              <ArrowUpRightIcon data-icon="inline-start" />
              Open page
            </Button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

const VIZ_LABEL: Record<InsightViz, string> = {
  number: "Number",
  bars: "Bars",
  line: "Line",
  table: "Table",
};

/** Pin an answer to Home (a quick modal: the page stays). */
export function PinDialog({
  api,
  co,
  result,
  onClose,
}: {
  api: Api;
  co: string;
  result: InsightResult;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [title, setTitle] = useState(result.title);
  const [viz, setViz] = useState<InsightViz>(defaultViz(result));
  const [admins, setAdmins] = useState(false);
  const pin = useMutation({
    mutationFn: () =>
      api.client.insights.widgets.create({
        params: { co },
        body: {
          title: title.trim(),
          spec: result.spec,
          viz,
          visibility: admins ? "admins" : "me",
        },
      }),
    onSuccess: () => {
      toast.add({ title: "Pinned to Home", type: "success" });
      void qc.invalidateQueries({ queryKey: widgetsKey(co) });
      onClose();
    },
    onError: (e) => toast.add({ title: problemText(e, "Not pinned"), type: "error" }),
  });
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-sm" data-slot="pin-dialog">
        <DialogHeader>
          <DialogTitle>Pin to Home</DialogTitle>
        </DialogHeader>
        <form
          id={`${id}-form`}
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) pin.mutate();
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${id}-title`}>Title</Label>
            <Input
              id={`${id}-title`}
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${id}-viz`}>Show as</Label>
            <NativeSelect
              id={`${id}-viz`}
              value={viz}
              onChange={(e) => setViz(e.target.value as InsightViz)}
            >
              {(Object.keys(VIZ_LABEL) as InsightViz[]).map((v) => (
                <NativeSelectOption key={v} value={v}>
                  {VIZ_LABEL[v]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-admins`}
              checked={admins}
              onCheckedChange={(on) => setAdmins(on === true)}
            />
            <Label htmlFor={`${id}-admins`} className="font-normal">
              Show to every owner and admin
            </Label>
            <InfoTip label="About sharing">
              Members never see pinned answers. Shared ones show on every owner's and admin's Home.
            </InfoTip>
          </div>
        </form>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            form={`${id}-form`}
            disabled={!title.trim() || pin.isPending}
          >
            Pin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
